import * as Yup from "yup";

// Common Validation Rules
export const requiredString = (message: string) =>
  Yup.string().required(message);
export const requiredNumber = (message: string) =>
  Yup.number().required(message);
export const requiredDate = (message: string) =>
  Yup.date().required(message).typeError("Invalid date format");
export const requiredFile = (message: string) => Yup.mixed().required(message);

// Reusable file validation function
const validateFile = (value: any, maxSizeMB: number = 1) => {
  // If value is a string, skip validation
  if (typeof value === "string") {
    return true;
  }

  // If value is null or undefined, fail the validation
  if (!value) {
    return false;
  }

  // If it's an array of files, ensure all files pass the validation
  if (Array.isArray(value)) {
    return value.every((file) => file.size <= maxSizeMB * 1024 * 1024);
  }

  // If the value is a single file, validate its size
  if (value instanceof File) {
    return value.size <= maxSizeMB * 1024 * 1024; // Convert MB to bytes
  }

  // For any other types, fail validation
  return false;
};

// Reusable Yup validation schema with the above function
export const fileValidationSchema = Yup.mixed().test(
  "is-valid-file",
  "File is required and must be less than 1 MB",
  (value) => validateFile(value)
);

export const emailRule = Yup.string()
  .email("Invalid email address")
  .required("Email is required");

export const passwordRule = Yup.string()
  .min(8, "Password must be at least 8 characters")
  .matches(/[A-Za-z]/, "Password must contain at least one letter")
  .matches(/[0-9]/, "Password must contain at least one number")
  .matches(/[@$!%*?&]/, "Password must contain at least one special character")
  .required("Password is required");

export const surveyFormModalValidationSchema = Yup.object({
  form_name: Yup.string().required("Form name is required"),
  description: Yup.string().required("Description is required"),
  status: Yup.string()
    .oneOf(["0", "1"], "Please select status")
    .required("Status is required"),
});
const noOptionQuestionTypes = ["1", "6", "7"];
export const surveyFormQuestionAddValidationSchema = Yup.object().shape({
  question_type: Yup.string()
    .oneOf(["2", "1", "3", "4", "5", "6", "7"], "Please select Question type")
    .required("Question type is required"),

  question_label: Yup.string()
    .max(500, "Max 500 characters")
    .required("Question label is required"),

  priority: Yup.number()
    .typeError("Priority must be a number")
    .min(1, "Priority must be at least 1")
    .required("Priority is required"),

  is_required: Yup.string()
    .oneOf(["0", "1"], "Please select Yes or No")
    .required("Required field is required"),

  options: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string()
          .max(255, "Option must be at most 255 characters")
          .required("Option is required"),
      })
    )
    .when(["question_type"], (values, schema) => {
      const [questionType] = values;

      return questionType && !noOptionQuestionTypes.includes(questionType)
        ? schema
          .min(1, "At least one option is required")
          .required("Options are required")
        : schema.test(
          "options-empty-when-no-option-type",
          "Options must be empty for selected question type",
          (value) => !value || value.length === 0
        );
    }),

});

export const getSurveyFormModalValidationSchema = (surveyFormDetails: any) => {
  const shape: Record<string, Yup.ObjectSchema<any>> = {};

  surveyFormDetails?.final_data?.forEach((q: any) => {
    const key = `q_${q.form_id}`;

    if (q.question_type === 3) {
      // Handle checkbox group
      const checkboxOptions = q.options.split("_");

      shape[key] = Yup.object().test(
        "at-least-one-checked",
        "This field is required",
        function (value: Record<string, boolean | undefined>) {
          if (q.is_required !== 1) return true;
          if (!value) return false;

          return checkboxOptions.some((opt: string) => value[`value_${opt}`]);
        }
      );
    } else {
      // Handle text/radio inputs
      const valueValidatorBase =
        q.question_type === 2
          ? Yup.string().oneOf(q.options.split("_"), "Invalid selection")
          : Yup.string().trim();

      const valueValidator =
        q.is_required === 1
          ? valueValidatorBase.required("This field is required")
          : valueValidatorBase.notRequired();

      shape[key] = Yup.object({
        value: valueValidator,
      });
    }
  });

  return Yup.object().shape(shape);
};
